import React, { useState } from 'react';
import { X, Share, Plus, Download, ArrowUp, MoreVertical } from 'lucide-react';
import {
  isIOS,
  isStandalone,
  isInstallable,
  promptInstall,
  markNudgeDismissed,
} from '../utils/installPrompt';

// Small bottom-sheet nudge that appears once after the user saves their first
// memory. On Chrome/Android we trigger the native prompt if it fired; on iOS
// and Android-without-a-captured-prompt we show a tiny manual walkthrough.
export default function InstallPWANudge({ open, onClose }) {
  const [installing, setInstalling] = useState(false);
  const [manualTip, setManualTip] = useState(null); // null | 'ios' | 'android'

  if (!open || isStandalone()) return null;

  const dismiss = () => {
    markNudgeDismissed();
    onClose && onClose();
  };

  const handleInstall = async () => {
    if (isIOS()) {
      setManualTip('ios');
      return;
    }
    if (!isInstallable()) {
      // Android Chrome / Samsung / Edge without a captured prompt event —
      // fall back to a generic "browser menu \u2192 Install" walkthrough.
      setManualTip('android');
      return;
    }
    setInstalling(true);
    const outcome = await promptInstall();
    setInstalling(false);
    if (outcome === 'accepted' || outcome === 'dismissed') {
      markNudgeDismissed();
      onClose && onClose();
    }
  };

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[60] px-3 pb-3 pointer-events-none"
      data-testid="pwa-install-nudge"
    >
      <div
        className="pointer-events-auto mx-auto max-w-sm bg-white rounded-2xl shadow-xl border border-neutral-200 overflow-hidden animate-fade-in-up"
      >
        <div className="flex items-start gap-3 px-4 pt-4 pb-3">
          <div className="w-11 h-11 rounded-xl bg-[#F7EFE1] flex items-center justify-center flex-shrink-0 text-2xl">
            🫙
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-serif-display text-[16.5px] font-semibold text-neutral-900 leading-tight">
              Save CuminJar to your home screen
            </p>
            <p className="mt-0.5 text-[12.5px] text-neutral-600 leading-snug">
              One tap. No login. Your family jar always ready — like a real app.
            </p>
          </div>
          <button
            type="button"
            onClick={dismiss}
            data-testid="pwa-nudge-dismiss"
            aria-label="Dismiss"
            className="w-8 h-8 rounded-full flex items-center justify-center text-neutral-400 hover:text-neutral-800 hover:bg-neutral-100 transition-colors"
          >
            <X size={15} />
          </button>
        </div>

        {!manualTip && (
          <div className="px-4 pb-4 flex gap-2">
            <button
              type="button"
              onClick={dismiss}
              data-testid="pwa-nudge-not-now"
              className="flex-1 text-[13px] font-medium text-neutral-700 py-2.5 rounded-lg hover:bg-neutral-50 transition-colors"
            >
              Not now
            </button>
            <button
              type="button"
              onClick={handleInstall}
              disabled={installing}
              data-testid="pwa-nudge-install"
              className="flex-[1.4] text-[13px] font-semibold bg-cumin-green text-white py-2.5 rounded-lg hover:bg-[#324A2F] transition-colors flex items-center justify-center gap-1.5 disabled:opacity-70"
            >
              <Download size={13} />
              {installing ? 'Adding\u2026' : (isIOS() || !isInstallable()) ? 'Show me how' : 'Add to Home Screen'}
            </button>
          </div>
        )}

        {manualTip === 'ios' && (
          <div className="border-t border-neutral-100 bg-[#FBF6EE] px-4 py-3.5" data-testid="pwa-nudge-ios-tip">
            <ol className="space-y-2 text-[12.5px] text-neutral-800">
              <li className="flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-white border border-neutral-200 flex items-center justify-center text-[10.5px] font-semibold text-cumin-green flex-shrink-0">1</span>
                <span className="flex items-center gap-1.5">Tap the <Share size={14} className="inline text-neutral-600" /> Share icon at the bottom.</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-white border border-neutral-200 flex items-center justify-center text-[10.5px] font-semibold text-cumin-green flex-shrink-0">2</span>
                <span className="flex items-center gap-1.5">Choose <Plus size={13} className="inline text-neutral-600" /> <b>Add to Home Screen</b>.</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-white border border-neutral-200 flex items-center justify-center text-[10.5px] font-semibold text-cumin-green flex-shrink-0">3</span>
                <span>Tap <b>Add</b>. CuminJar now lives on your home screen.</span>
              </li>
            </ol>
            <p className="mt-3 flex items-center justify-center gap-1 text-[11px] text-neutral-500">
              <ArrowUp size={11} /> Look for the Share icon in Safari&rsquo;s toolbar.
            </p>
            <button
              type="button"
              onClick={dismiss}
              data-testid="pwa-nudge-ios-done"
              className="mt-3 w-full text-[12.5px] font-semibold bg-cumin-green text-white py-2 rounded-lg hover:bg-[#324A2F] transition-colors"
            >
              Got it
            </button>
          </div>
        )}

        {manualTip === 'android' && (
          <div className="border-t border-neutral-100 bg-[#FBF6EE] px-4 py-3.5" data-testid="pwa-nudge-android-tip">
            <ol className="space-y-2 text-[12.5px] text-neutral-800">
              <li className="flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-white border border-neutral-200 flex items-center justify-center text-[10.5px] font-semibold text-cumin-green flex-shrink-0">1</span>
                <span className="flex items-center gap-1.5">Tap the <MoreVertical size={14} className="inline text-neutral-600" /> menu at the top right of your browser.</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-white border border-neutral-200 flex items-center justify-center text-[10.5px] font-semibold text-cumin-green flex-shrink-0">2</span>
                <span>Choose <b>Install app</b> or <b>Add to Home screen</b>.</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-white border border-neutral-200 flex items-center justify-center text-[10.5px] font-semibold text-cumin-green flex-shrink-0">3</span>
                <span>Tap <b>Install</b>. CuminJar now lives on your home screen.</span>
              </li>
            </ol>
            <p className="mt-3 flex items-center justify-center gap-1 text-[11px] text-neutral-500">
              <ArrowUp size={11} /> Look for the three dots menu in your browser toolbar.
            </p>
            <button
              type="button"
              onClick={dismiss}
              data-testid="pwa-nudge-android-done"
              className="mt-3 w-full text-[12.5px] font-semibold bg-cumin-green text-white py-2 rounded-lg hover:bg-[#324A2F] transition-colors"
            >
              Got it
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
